import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { DatePipe, LowerCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { Api } from '@services/api.service';
import { AuthService } from '@services/auth.service';
import { ToastService } from '@services/toast.service';
import { IOrgUserView } from '@common-interfaces/org-user';
import { presetForScopes } from '../../../../../../../../../common/common-types/user-scopes';
import { PageHeaderComponent } from '../../../../components/shared/page-header/page-header.component';
import { EmptyStateComponent } from '../../../../components/shared/empty-state/empty-state.component';
import { debugLogger } from '@common-helpers/debug-logger';

interface UserRow extends IOrgUserView {
    name: string;
    initials: string;
    roleKey: string;
}

@Component({
    selector: 'user-administration',
    imports: [
        TableModule,
        ButtonModule,
        TagModule,
        InputTextModule,
        IconFieldModule,
        InputIconModule,
        FormsModule,
        DatePipe,
        LowerCasePipe,
        RouterLink,
        TranslatePipe,
        PageHeaderComponent,
        EmptyStateComponent
    ],
    templateUrl: './user-administration.component.html'
})
export class UserAdministrationComponent implements OnInit {
    private api = inject(Api);
    private auth = inject(AuthService);
    private router = inject(Router);
    private toast = inject(ToastService);

    loading = signal(true);
    search = signal('');
    private users = signal<IOrgUserView[]>([]);

    venueCount = computed(() => this.auth.clientsInOrganization().length);
    orgId = this.auth.organizationId;

    rows = computed<UserRow[]>(() => {
        const term = this.search().trim().toLowerCase();
        return this.users()
            .map((user) => {
                const name = `${user.firstName} ${user.lastName}`.trim() || user.email;
                const preset = presetForScopes(user.scopes);
                return {
                    ...user,
                    name,
                    initials: ((user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '')).toUpperCase() || user.email[0]?.toUpperCase(),
                    roleKey: preset ? `ROLE_${preset}` : user.scopes.length ? 'ROLE_CUSTOM' : 'ROLE_NONE'
                };
            })
            .filter((row) => !term || row.name.toLowerCase().includes(term) || row.email.toLowerCase().includes(term));
    });

    async ngOnInit(): Promise<void> {
        try {
            const res = await this.api.currentorganization().get<IOrgUserView[]>('/users');
            if (res.success) {
                this.users.set(res.data);
            }
        } catch (error) {
            debugLogger.error('Failed to load users', error);
            this.toast.showError('Could not load users.');
        } finally {
            this.loading.set(false);
        }
    }

    open(user: UserRow): void {
        this.router.navigate(['/organizations', this.orgId(), 'admin', 'users', user._id]);
    }

    venueLabel(user: UserRow): string {
        const total = this.venueCount();
        return total > 0 && user.associatedClients.length >= total ? 'ALL_VENUES' : '';
    }
}
